import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { motion } from "framer-motion";
import { Trophy, Users, IndianRupee, Stethoscope, Target, Clock, UserPlus, Info } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import DoctorLeaderboard from "@/components/DoctorLeaderboard";
import { useAuth } from "@/lib/auth";

const RANGE_OPTIONS = [
  { value: 7,   label: "7 days"  },
  { value: 30,  label: "30 days" },
  { value: 90,  label: "90 days" },
  { value: 180, label: "6 months" },
];

const METRIC_ICONS = {
  revenue:              IndianRupee,
  appts_completed:      Users,
  patients_seen:        Users,
  treatments_completed: Stethoscope,
  completion_rate:      Target,
  on_time_rate:         Clock,
  new_patients:         UserPlus,
};

function fmtINR(n) {
  if (n == null) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function MetricBar({ metric, rows, label }) {
  const isCurrency = metric === "revenue";
  const isPct = metric === "completion_rate" || metric === "on_time_rate";
  const data = [...rows]
    .map((r) => ({ name: r.name.replace(/^Dr\.\s*/, ""), value: r[metric] ?? 0, doctor_id: r.doctor_id }))
    .sort((a, b) => b.value - a.value);
  const max = Math.max(...data.map((d) => d.value), 1);
  const Icon = METRIC_ICONS[metric] || Users;

  return (
    <div className="card-widget" data-testid={`metric-card-${metric}`}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-full bg-linen grid place-items-center text-ink">
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <div className="overline text-ink-soft">Ranked by</div>
          <div className="font-display text-lg leading-tight">{label}</div>
        </div>
      </div>
      <div style={{ height: Math.max(180, data.length * 40) }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 20, bottom: 4, left: 8 }}>
            <XAxis type="number"
                   tickFormatter={(v) => isCurrency ? `₹${Math.round(v / 1000)}k` : isPct ? `${Math.round(v)}%` : v}
                   stroke="#9A9E9C" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis type="category" dataKey="name" width={100}
                   stroke="#9A9E9C" fontSize={12} tickLine={false} axisLine={false} />
            <Tooltip
              formatter={(v) => isCurrency ? fmtINR(v) : isPct ? `${Math.round(v)}%` : v}
              contentStyle={{ borderRadius: 12, border: "1px solid #E8EAE9", fontSize: 12 }}
            />
            <Bar dataKey="value" radius={[6, 6, 6, 6]}>
              {data.map((d, i) => (
                <Cell key={d.doctor_id} fill={i === 0 ? "#1A362B" : i === 1 ? "#4A6B5D" : "#D06A42"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function DoctorAnalytics() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data: d } = await api.get(`/leaderboard/doctors?days=${days}`);
      setData(d);
      setError(null);
    } catch (e) {
      setError(apiError(e, "Failed to load analytics"));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const canSeeRevenue = user?.role !== "doctor";

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                data-testid="doctor-analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="overline text-ink-soft">Analytics</span>
          <h1 className="mt-2 font-display text-4xl tracking-tight flex items-center gap-3">
            <Trophy className="w-8 h-8 text-ink" /> Doctor performance
          </h1>
          <p className="mt-2 text-sm text-ink-mid max-w-xl">
            How each doctor is contributing across revenue, patient care, and clinical throughput.
            Metrics are relative to the clinic&apos;s top performer in the selected window.
          </p>
        </div>
        <div className="inline-flex rounded-full border border-[#E8EAE9] p-1 bg-white text-xs"
             data-testid="analytics-range-toggle">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.value}
              data-testid={`analytics-range-${r.value}`}
              onClick={() => setDays(r.value)}
              className={`px-3 py-1.5 rounded-full transition ${
                days === r.value ? "bg-pine text-linen" : "text-ink-mid hover:text-ink"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Full leaderboard (non-compact, shows all doctors) */}
      <DoctorLeaderboard compact={false} defaultDays={days} showViewAll={false} key={days} />

      {loading && (
        <div className="mt-8 py-16 text-center text-sm text-ink-soft">Loading analytics…</div>
      )}

      {!loading && error && (
        <div className="mt-8 py-8 text-sm text-[#B94A48] bg-[#FCECEC] rounded-xl px-4">{error}</div>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          {/* Per-metric breakdown charts */}
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <div className="overline text-ink-soft">Performance Trend Overview</div>
              <Info className="w-3.5 h-3.5 text-ink-soft" title="Ranked bar charts show relative performance per metric." />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {canSeeRevenue && <MetricBar metric="revenue" rows={rows} label="Revenue collected" />}
              <MetricBar metric="appts_completed"      rows={rows} label="Appointments completed" />
              <MetricBar metric="patients_seen"        rows={rows} label="Unique patients seen" />
              <MetricBar metric="treatments_completed" rows={rows} label="Treatments completed" />
              <MetricBar metric="completion_rate"      rows={rows} label="Treatment completion rate" />
              <MetricBar metric="on_time_rate"         rows={rows} label="On-time / show-up rate" />
              {canSeeRevenue && <MetricBar metric="new_patients" rows={rows} label="New patient acquisitions" />}
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
}
